import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export const ConditionalSkipGuide: React.FC = () => {
  const webhookUrl = 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/conditional-skip';
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const sampleEnterSignal = `{
  "action": "ENTER_LONG",
  "instrument": "ETHUSD.P",
  "timestamp": "2025-08-27T06:39:00Z",
  "signalToken": "bc579485-ea32-4082-9f6b-4c102fca183d",
  "maxLag": "300",
  "investmentType": "contract",
  "amount": "1",
  "stop_loss": "4588.5046",
  "take_profit": "4606.8954",
  "relative_stop_loss": "919539.9999999609",
  "relative_take_profit": "919539.9999999609"
}`;

  const sampleExitSignal = `{
  "action": "EXIT_LONG",
  "instrument": "ETHUSD.P",
  "timestamp": "2025-08-27T06:49:00Z",
  "signalToken": "bc579485-ea32-4082-9f6b-4c102fca183d",
  "maxLag": "300",
  "investmentType": "contract",
  "amount": "1",
  "win": "1"
}`;

  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle>What is Conditional Skip?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Conditional Skip acts as an intelligent filter between TradingView and your trading bot. 
            It monitors consecutive losses and only forwards ENTER signals after a configurable 
            number of consecutive losses, helping you implement martingale-like strategies.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">How it works:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Tracks win/loss results per bot</li>
                <li>• Counts consecutive losses</li>
                <li>• Filters ENTER signals based on loss streak</li>
                <li>• Forwards signals when threshold is met</li>
                <li>• Resets after a winning trade</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Benefits:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Reduce unnecessary trades</li>
                <li>• Wait for optimal entry points</li>
                <li>• Implement strategic patience</li>
                <li>• Full audit trail</li>
                <li>• Per-bot configuration</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mode Explanation */}
      <Card>
        <CardHeader>
          <CardTitle>Bot States</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">GATE</Badge>
                <span className="font-medium">Closed Mode</span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Bot is waiting for the loss streak threshold to be reached.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• ENTER signals are filtered</li>
                <li>• EXIT signals update loss streak</li>
                <li>• Switches to OPEN when threshold met</li>
              </ul>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="default">OPEN</Badge>
                <span className="font-medium">Active Mode</span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Bot forwards all signals until a winning trade occurs.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• All ENTER signals forwarded</li>
                <li>• All EXIT signals forwarded</li>
                <li>• Resets to GATE on win</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-1">1</Badge>
              <div>
                <h4 className="font-medium">Configure Bot Settings</h4>
                <p className="text-sm text-muted-foreground">
                  Go to the Settings tab and add your bot with desired loss streak threshold and forward URL.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-1">2</Badge>
              <div>
                <h4 className="font-medium">Update TradingView Webhook</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Change your TradingView alert webhook URL to:
                </p>
                <div className="flex items-center gap-2 p-2 bg-muted rounded-md font-mono text-sm">
                  <span className="flex-1">{webhookUrl}</span>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard(webhookUrl)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-1">3</Badge>
              <div>
                <h4 className="font-medium">Monitor and Adjust</h4>
                <p className="text-sm text-muted-foreground">
                  Use the Monitor tab to watch real-time processing and adjust thresholds as needed.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signal Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Signal Examples</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">ENTER Signal</h4>
              <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                {sampleEnterSignal}
              </pre>
              <Button 
                size="sm" 
                variant="ghost" 
                className="mt-2"
                onClick={() => copyToClipboard(sampleEnterSignal)}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">EXIT Signal</h4>
              <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                {sampleExitSignal}
              </pre>
              <Button 
                size="sm" 
                variant="ghost" 
                className="mt-2"
                onClick={() => copyToClipboard(sampleExitSignal)}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Important Notes:</h4>
            <ul className="text-sm space-y-1">
              <li>• The <code>win</code> field in EXIT signals determines win (1) or loss (0)</li>
              <li>• The <code>signalToken</code> must match your bot's signal token</li>
              <li>• Signals are forwarded to your configured <code>forward_url</code></li>
              <li>• All signal processing is logged for debugging</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Example Scenario */}
      <Card>
        <CardHeader>
          <CardTitle>Example Scenario</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm font-medium">
              Threshold set to 4 consecutive losses:
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 1</Badge>
                <span>ENTER_LONG → WIN</span>
                <Badge variant="secondary">SKIP</Badge>
                <span className="text-muted-foreground">Reset streak to 0</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 2</Badge>
                <span>ENTER_LONG → LOSS</span>
                <Badge variant="secondary">SKIP</Badge>
                <span className="text-muted-foreground">Streak: 1</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 3</Badge>
                <span>ENTER_LONG → LOSS</span>
                <Badge variant="secondary">SKIP</Badge>
                <span className="text-muted-foreground">Streak: 2</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 4</Badge>
                <span>ENTER_LONG → LOSS</span>
                <Badge variant="secondary">SKIP</Badge>
                <span className="text-muted-foreground">Streak: 3</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 5</Badge>
                <span>ENTER_LONG → LOSS</span>
                <Badge variant="secondary">SKIP</Badge>
                <span className="text-muted-foreground">Streak: 4</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 6</Badge>
                <span>ENTER_LONG</span>
                <Badge variant="default">FORWARD</Badge>
                <span className="text-muted-foreground">Threshold reached → OPEN mode</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="default">Signal 7</Badge>
                <span>EXIT_LONG → WIN</span>
                <Badge variant="default">FORWARD</Badge>
                <span className="text-muted-foreground">Reset to GATE mode</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategy Groups (Advanced Feature) */}
      <Card>
        <CardHeader>
          <CardTitle>Strategy Groups (Advanced)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Strategy Groups allow you to coordinate multiple bots based on priority. 
            Only one bot per group will forward signals at a time, preventing conflicts 
            between similar strategies.
          </p>
          
          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
            <h4 className="font-medium mb-2">How it works:</h4>
            <ul className="text-sm space-y-1">
              <li>• Create a group (e.g., "XAUUSD Trend Strategies")</li>
              <li>• Add bots with priorities (1 = highest, 2 = lower, etc.)</li>
              <li>• When no bot is active, highest priority OPEN bot gets selected</li>
              <li>• Once active, bot runs its sequence until it closes its gate</li>
              <li>• Other bots wait even if they have signals</li>
              <li>• When active bot closes gate, next priority bot can take over</li>
            </ul>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-2">Example Group Setup:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">P1</Badge>
                  <span>XAUUSD-TR-A (Main Trend Strategy)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">P2</Badge>
                  <span>XAUUSD-TR-B (Alternative Trend)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">P3</Badge>
                  <span>XAUUSD-SW-A (Sideways Backup)</span>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-2">Priority Selection:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Lower number = higher priority</li>
                <li>• Only OPEN mode bots can be selected</li>
                <li>• Active bot keeps lock until gate closes</li>
                <li>• Each bot uses its own flexible patterns</li>
                <li>• Groups add coordination layer on top</li>
              </ul>
            </div>
          </div>
          
          <div className="border-l-4 border-yellow-500 pl-4 py-2">
            <p className="text-sm">
              <strong>Important:</strong> Each bot in the group still uses its own Type 2 Flexible Patterns. 
              Strategy Groups add an additional layer of coordination on top, preventing multiple bots 
              from the same group from trading simultaneously.
            </p>
          </div>
          
          <div className="space-y-2">
            <h4 className="font-medium">Use Cases:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Trend vs Sideways:</strong> Coordinate TR and SW strategies for same instrument</li>
              <li>• <strong>Multi-timeframe:</strong> Prioritize higher timeframe strategies over lower</li>
              <li>• <strong>Strategy Testing:</strong> Test new strategies with lower priority while keeping proven ones active</li>
              <li>• <strong>Risk Management:</strong> Ensure only one aggressive strategy runs at a time</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};